import { formatDateTime, type FormatDateTimeOptions } from '../../../config/appConfig';
import { getBrowserTimeZone, formatTimeZoneLabel, formatTimeZoneOffset } from '../../../utils/timeZone';
import type { ParsedEmailDateHeader } from './emailDateHeader';

export interface EmailDateHintRow {
  id: string;
  label: string;
  time: string;
  offset: string;
  description: string;
}

export type ReceivedLagTone = 'normal' | 'warning' | 'neutral';

export interface ReceivedLagInfo {
  text: string;
  tone: ReceivedLagTone;
}

export interface EmailDateHintMailboxRow {
  id: string;
  label: string;
  time: string;
  description?: string;
}

export interface EmailDateHintContent {
  summary: string;
  primaryLabel: string;
  primaryDescription: string;
  /** Message sent time in other zones (same instant as Date header). */
  zoneRows: EmailDateHintRow[];
  /** Per-mailbox breakdown (e.g. last sync footer). */
  mailboxRows?: EmailDateHintMailboxRow[];
  received?: {
    time: string;
    utcTime: string;
    offset: string;
    lag: ReceivedLagInfo;
  };
  headerRaw?: string;
  headerOffset?: string;
  headerDescription?: string;
}

export interface BuildEmailDateHintContentOptions {
  zoneCaption: string;
  effectiveZone: string;
  displayPreference?: string;
  date: string;
  formatOpts?: FormatDateTimeOptions;
  headerInfo: ParsedEmailDateHeader;
  receivedDate?: string;
}

function formatInZone(date: string, zone: string, formatOpts?: FormatDateTimeOptions): string {
  return formatDateTime(date, { ...formatOpts, timeZone: zone });
}

function formatReceivedLag(sent: string, received: string): ReceivedLagInfo {
  const sentMs = new Date(sent).getTime();
  const receivedMs = new Date(received).getTime();
  if (Number.isNaN(sentMs) || Number.isNaN(receivedMs)) {
    return {
      text: 'When your mail server fetched and stored this copy',
      tone: 'neutral',
    };
  }
  if (sentMs === receivedMs) {
    return {
      text: 'No delay to receive the message — normal',
      tone: 'normal',
    };
  }

  const diffMs = receivedMs - sentMs;
  const absMs = Math.abs(diffMs);
  const minutes = Math.round(absMs / 60_000);
  const hours = Math.round(absMs / 3_600_000);
  const days = Math.round(absMs / 86_400_000);

  let duration: string;
  if (minutes < 1) duration = 'Less than a minute';
  else if (minutes < 90) duration = `${minutes} minute${minutes === 1 ? '' : 's'}`;
  else if (hours < 48) duration = `${hours} hour${hours === 1 ? '' : 's'}`;
  else duration = `${days} day${days === 1 ? '' : 's'}`;

  if (diffMs > 0) {
    return {
      text: `${duration} delay to receive the message — normal`,
      tone: 'normal',
    };
  }
  return {
    text: `${duration} before sent time — unusual (possible clock skew)`,
    tone: 'warning',
  };
}

export function buildEmailDateHintContent({
  zoneCaption,
  effectiveZone,
  displayPreference,
  date,
  formatOpts,
  headerInfo,
  receivedDate,
}: BuildEmailDateHintContentOptions): EmailDateHintContent {
  const browserZone = getBrowserTimeZone();
  const zoneRows: EmailDateHintRow[] = [];
  const seenZones = new Set<string>();

  const addZoneRow = (
    id: string,
    zone: string,
    label: string,
    description: string,
  ) => {
    if (seenZones.has(zone)) return;
    seenZones.add(zone);
    const time = formatInZone(date, zone, formatOpts);
    if (!time) return;
    zoneRows.push({
      id,
      label,
      time,
      offset: formatTimeZoneOffset(zone),
      description,
    });
  };

  addZoneRow(
    'display',
    effectiveZone,
    displayPreference ? formatTimeZoneLabel(displayPreference) : 'Browser default',
    displayPreference
      ? 'Sent time in your display timezone'
      : `Sent time in your computer timezone (${formatTimeZoneLabel(browserZone)})`,
  );

  addZoneRow(
    'utc',
    'UTC',
    'UTC',
    'Sent time in UTC',
  );

  if (displayPreference && browserZone !== effectiveZone) {
    addZoneRow(
      'browser',
      browserZone,
      formatTimeZoneLabel(browserZone),
      'Sent time on this device',
    );
  }

  let received: EmailDateHintContent['received'];
  if (receivedDate) {
    const receivedFormatted = formatInZone(receivedDate, effectiveZone, formatOpts);
    const receivedUtc = formatInZone(receivedDate, 'UTC', formatOpts);
    if (receivedFormatted) {
      received = {
        time: receivedFormatted,
        utcTime: receivedUtc || receivedFormatted,
        offset: formatTimeZoneOffset(effectiveZone),
        lag: formatReceivedLag(date, receivedDate),
      };
    }
  }

  let headerDescription: string | undefined;
  if (headerInfo.offsetLabel) {
    headerDescription = `The sender's mail app wrote the Date header with offset ${headerInfo.offsetLabel}. That is a label in the message, not necessarily where they were.`;
  } else if (headerInfo.rawLine) {
    headerDescription = 'Original Date line from the email headers.';
  }

  return {
    summary: zoneCaption,
    primaryLabel: 'Sent (Date header)',
    primaryDescription: 'When the sender\'s mail app claims the message was sent',
    zoneRows,
    received,
    headerRaw: headerInfo.rawLine,
    headerOffset: headerInfo.offsetLabel,
    headerDescription,
  };
}
